import { useState } from 'react';
import { PendingFirstBuild, ServiceEnvironment } from '@back4app2/sdk';
import CustomerErrorMessage from './CustomErrorMessage';
import PreDeployConfirmModal from '../PreDeployConfirmModal';
import Button from '../Button';
import { ReactComponent as CircleExclamationSVG } from '../../assets/images/circle-exclaimation-icon.svg';

/**
 * The overview's version of the pre-deploy screen: a notice, then a modal.
 *
 * The panel used to render inline here, and it was wrong in a way that had
 * nothing to do with its styling — the overview is a board of panels (plan,
 * CPU, RAM, deployments, guides) and a long form dropped into the middle of it
 * pushes all of that below the fold. This is a decision to make once, not a
 * permanent part of the page.
 *
 * So it reads like every other state this page reports — payment pending,
 * unverified email, security block: one card, one sentence, one button. The
 * form itself lives where forms live, in a modal, and is the SAME component
 * the wizard and the Deploy button use.
 */
const PendingSetupMessage = ({
  appId,
  serviceEnvironment,
  pendingFirstBuild,
  onReleased,
}: {
  appId: string;
  serviceEnvironment: ServiceEnvironment;
  pendingFirstBuild: PendingFirstBuild;
  onReleased: () => Promise<void> | void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <CustomerErrorMessage
        type="warn"
        icon={
          <CircleExclamationSVG
            width="27px"
            height="26px"
            className="text-alert-yellow flex-none"
          />
        }
        headerText="Finish setting up your web application"
        description={
          <>
            <div className="text-sm mb-6">
              We read your project before building it and have a few things to
              confirm. Your first build starts as soon as you&apos;re done —
              none of it is required.
            </div>
            <Button type="primary" onClick={() => setOpen(true)}>
              Review setup
            </Button>
          </>
        }
      />

      {open ? (
        <PreDeployConfirmModal
          appId={appId}
          serviceEnvironment={serviceEnvironment}
          runAssistance={pendingFirstBuild.runAssistance}
          proceedLabel="Save and start"
          skipLabel="Start without changes"
          onCancel={() => setOpen(false)}
          onProceed={async () => {
            setOpen(false);
            await onReleased();
          }}
        />
      ) : null}
    </>
  );
};

export default PendingSetupMessage;
